import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'

import searchCacheStateManager from '@src/quince-site-common/src/redux/globalStateManager/SearchCacheStateManager'

import { getSearchCacheKey } from '../utils'

const useSearchCache = (searchParams: URLSearchParams) => {
  // cacheKey is the key for the search cache
  const cacheKey = getSearchCacheKey(searchParams)

  const router = useRouter()

  const cacheData = searchCacheStateManager.getSearchCache(cacheKey)

  // currentCacheKey is the key for the current search cache
  const currentCacheKey = useRef(cacheKey)
  // currentCacheKey.current = cacheKey

  // scrollY is the scroll position of the page
  const scrollY = useRef(0)

  useEffect(() => {
    // update the current cache key when the cache key changes
    currentCacheKey.current = cacheKey
  }, [cacheKey])

  // Restore scroll position when search params change
  useEffect(() => {
    if (cacheData?.scrollY) {
      // Use a MutationObserver to detect when content is added to the DOM
      const observer = new MutationObserver(mutations => {
        const hasContentChanges = mutations.some(
          mutation => mutation.type === 'childList' && mutation.addedNodes.length > 0,
        )

        if (hasContentChanges) {
          window.scrollTo(0, cacheData.scrollY)
          observer.disconnect()
        }
      })

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      })

      return () => {
        observer.disconnect()
      }
    }
    return undefined
  }, [searchParams])

  useEffect(() => {
    // Store the scroll position of the page
    const handleScroll = () => {
      scrollY.current = window.scrollY
    }

    // Store the scroll position of the page in the search cache when the route changes
    const handleRouteChange = () => {
      if (cacheData) {
        searchCacheStateManager.publishSearchCache(currentCacheKey.current, scrollY.current)
      }
    }

    // Add the event listener for the scroll event to store the scroll position
    window.addEventListener('scroll', handleScroll, { passive: true })
    // Add the event listener for the route change event to store the scroll position in the search cache
    router.events.on('routeChangeStart', handleRouteChange)

    return () => {
      // Remove the event listener for the scroll event
      window.removeEventListener('scroll', handleScroll)
      // Remove the event listener for the route change event
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [router.events, cacheData])

  return {
    currentCacheKey,
    scrollY,
    cacheData,
  }
}

export default useSearchCache
